import 'firebase/compat/auth';
import Loader from 'tailwind-components/Loader/Loader';
import firebase from 'firebase/compat/app';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation
} from 'react-router-dom';
import PrivateRoute from 'routes/PrivateRoute';
import {
  getDashboardData,
  LogOut,
  syncViewAsFromSearch
} from 'state/actions/actions';
import { getNotifications } from 'state/actions/notifications';
import { siteLoading, siteLoaded } from 'state/actions/site';
import AgencyManagement from 'views/AgencyManagement';
import ONeillAgencyDashboard from 'views/AgencyManagement/NewAgencyDashboard';
import SunshineAgencyDashboard from 'views/AgencyManagement/AgencyDashboard';
import AgencySettings from 'views/AgencyManagement/AgencySettings';
import AgentInfo from 'views/AgentInfo';
import CarrierResources from 'views/CarrierResources';
import Resources from 'views/Resources';
import Commissions from 'views/Commissions';
import Dashboard from 'views/Dashboard';
import FAQs from 'views/FAQs';
import MyAppointmentsView from 'views/MyAppointments';
import OepUpdates from 'views/OepUpdates';
import Registration from 'views/Registration/Registration';
import ResetPassword from 'views/ResetPassword';
import PrivateRouteAuth from './PrivateRouteAuth';
import ViewAsAgentSync from './ViewAsAgentSync';
import Bookshelf from 'views/Bookshelf';
import ChangePasswordMFA from 'views/ChangePassword/ChangePasswordMFA';
import ChangePhoneMFA from 'views/ChangePhone/ChangePhoneMFA';
import DashboardBob from 'views/DashboardBob';
import DashboardCommissionReco from 'views/DashboardCommissionReco';
import DashboardCommissions from 'views/DashboardCommissions';
import DashboardCustomers from 'views/DashboardCustomers';
import Login from 'views/Login';
import LoginMFA from 'views/Login/LoginMFA';
import ContentLibrary from 'views/ContentLibrary';
import MarketingLeadPartners from 'views/Marketing/LeadPartners';
import AgencyAppointments from 'views/AgencyManagement/AgencyAppointments';
import PrivacyPolicy from 'views/PrivacyPolicy';
import ForceChangePassword from 'views/ForceChangePassword/ForceChangePassword';
import AgencyHierarchy from 'tailwind-components/AgencyHierarchy';

import { getDefaultAuthenticatedPath } from 'helpers/defaultAuthenticatedPath';
import { getCompany } from 'helpers/getCompanyVariables';
import ReconciliationForms from 'views/ReconciliationForms';
import { isAgentRankLimited } from 'helpers/agents';
import { resetVisitor, applyVisitor } from '../services/salesiq';
import { USER_TYPES } from 'constants/enum';
import { SET_MUST_CHANGE_PASSWORD } from 'constants/types';
import {
  isCallReviewsTabVisible,
  isLeadPartnersTabVisible
} from 'helpers/viewing-rules';
import useViewAs from 'hooks/useViewAs';
import CallReviews from 'views/CallReviews';

const company = getCompany();

function DefaultRedirect({ user }) {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: getDefaultAuthenticatedPath(user),
        search: location.search
      }}
    />
  );
}

DefaultRedirect.propTypes = {
  user: PropTypes.object.isRequired
};

// redirect an old (camelCase) path to its new kebab-case path, preserving the query string.
function RedirectWithQuery({ to }) {
  const location = useLocation();
  return <Navigate replace to={{ pathname: to, search: location.search }} />;
}

RedirectWithQuery.propTypes = {
  to: PropTypes.string.isRequired
};

function RoutesComponent() {
  const isLoadingGlobal = useSelector(state => state.site.loadingGlobal);
  const user = useSelector(state => state.user.zohoData);
  const needsUserRefresh = useSelector(state => state.user.needsRefresh);
  const { targetId: viewAsAgent } = useViewAs();
  const { agencyData } = user;
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  const showBobCommissionsTab = !user.Hide_BOB_Commissions_Tab;
  const showCommissionsTab = !user.Hide_Customers_Commissions_Tab;
  const showLeadPartnersTab = isLeadPartnersTabVisible(user);
  const showCallReviews = isCallReviewsTabVisible(user, viewAsAgent);

  const appointmentsDisabled =
    company === 'oneill'
      ? false
      : user.Rank === 'AmeriHealth Only' &&
        user.Account_Name?.id === '6291220000047935206';

  const isAgentLimited = isAgentRankLimited(user.Rank);

  const dispatch = useDispatch();

  useEffect(() => {
    globalThis.syncNativeAuth = async customToken => {
      try {
        await firebase.auth().signInWithCustomToken(customToken);
      } catch (error) {
        console.error('Failed to sync webview auth', error);
      }
    };

    firebase.auth().onAuthStateChanged(async firebaseUser => {
      if (!firebaseUser || agencyData === null) {
        resetVisitor();
      }
      if (!firebaseUser) {
        if (globalThis.ReactNativeWebView) {
          globalThis.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'REQUEST_AUTH_TOKEN' })
          );
          return;
        }
        dispatch(LogOut());
      } else if (agencyData === null) {
        dispatch(LogOut());
      } else {
        setFirebaseUser(firebaseUser);
        // read custom claims before routes render so the password gate is accurate on reload
        const tokenResult = await firebaseUser.getIdTokenResult();
        dispatch({
          type: SET_MUST_CHANGE_PASSWORD,
          payload: tokenResult.claims.mustChangePassword === true
        });
        dispatch(getNotifications());
        const search = globalThis.location?.search ?? '';
        const hasViewAsParam = new URLSearchParams(search).has('va');
        const bootstrapSession = async () => {
          // only show the global loader when a profile request is actually going out
          const willFetchProfile = hasViewAsParam || needsUserRefresh;
          if (willFetchProfile) dispatch(siteLoading());
          const { denied } = await dispatch(syncViewAsFromSearch(search));
          if (willFetchProfile) dispatch(siteLoaded());
          if (denied) {
            // view-as denied on initial load: strip ?va so ViewAsAgentSync
            // (mounted once the loader clears) doesn't retrigger it
            globalThis.history?.replaceState?.(
              null,
              '',
              globalThis.location.pathname
            );
          }
          dispatch(getDashboardData());
        };
        void bootstrapSession();
      }
      setIsLoadingUser(false);
    });
  }, []);

  useEffect(() => {
    if (firebaseUser && agencyData !== null && !viewAsAgent) {
      applyVisitor({
        agentFirstName: user.First_Name,
        agentLastName: user.Last_Name,
        agentEmail: user.Email
      });
    }
  }, [firebaseUser, agencyData, viewAsAgent]);

  return (
    <>
      {isLoadingGlobal || isLoadingUser ? (
        <Loader global />
      ) : (
        <Router>
          <ViewAsAgentSync />
          <Routes>
            <Route element={<PrivateRouteAuth />}>
              <Route
                path='/login'
                element={company === 'oneill' ? <LoginMFA /> : <Login />}
              />
              <Route path='/reset-password' element={<ResetPassword />} />
              <Route path='/registration' element={<Registration />} />
            </Route>
            <Route element={<PrivateRoute />}>
              {/* Routes for all users */}
              <Route path='/' element={<DefaultRedirect user={user} />} />
              <Route path='/home' element={<Dashboard />} />
              <Route path='/my-profile' element={<AgentInfo />} />
              {/* Routes for agency management */}
              {user.Position_at_Agency === USER_TYPES.ADMIN &&
                user.Account_Name && (
                  <>
                    {company === 'sunshine' ? (
                      <>
                        <Route
                          path='/agencies'
                          element={<AgencyManagement />}
                        />
                        <Route
                          exact
                          path='/agencies/:id'
                          element={<SunshineAgencyDashboard />}
                        />
                        <Route
                          exact
                          path='/agencies/:id/settings'
                          element={<AgencySettings />}
                        />
                      </>
                    ) : (
                      <>
                        <Route
                          exact
                          path='/agency/appointments/:id'
                          element={<AgencyAppointments />}
                        />
                        <Route
                          path='/agency/hierarchy'
                          element={<AgencyHierarchy />}
                        />
                        <Route
                          exact
                          path='/agency/dashboard/:id'
                          element={<ONeillAgencyDashboard />}
                        />
                        <Route
                          exact
                          path='/agency/settings/:id'
                          element={<AgencySettings />}
                        />
                      </>
                    )}
                  </>
                )}
              <Route path='/commissions/:type' element={<Commissions />} />
              <Route path='/change-password' element={<ChangePasswordMFA />} />
              <Route
                path='/change-2fa-phone'
                element={<ChangePhoneMFA noReset />}
              />

              {/* Routes for active users */}
              {!isAgentLimited && (
                <>
                  <Route path='/handbook' element={<Bookshelf />} />
                  <Route path='/off-market/bookshelf' element={<Bookshelf />} />
                  <Route
                    path='/customers/policies'
                    element={<DashboardCustomers />}
                  />
                  <Route
                    path='/customers/book-of-business'
                    element={<DashboardBob />}
                  />
                  {company === 'sunshine' && (
                    <Route
                      path='/customers/risk-management'
                      element={<DashboardBob riskManagement />}
                    />
                  )}
                  {showBobCommissionsTab && (
                    <Route
                      path='/customers/commission-reconciliation'
                      element={<DashboardCommissionReco />}
                    />
                  )}
                  {showCommissionsTab && (
                    <Route
                      path='/commissions/overview'
                      element={<DashboardCommissions />}
                    />
                  )}
                  <Route
                    path='/carrier-resources'
                    element={<CarrierResources />}
                  />
                  <Route path='/resources' element={<Resources />} />
                  <Route path='/faqs' element={<FAQs />} />
                  <Route
                    path='/faqs/:faqId/:questionNumber'
                    element={<FAQs />}
                  />
                  {!appointmentsDisabled && (
                    <>
                      <Route
                        path='/carrier-appointments'
                        element={<MyAppointmentsView />}
                      />
                      <Route
                        path='/myAppointments'
                        element={
                          <RedirectWithQuery to='/carrier-appointments' />
                        }
                      />
                    </>
                  )}
                  {company === 'oneill' && (
                    <>
                      <Route path='/oep-updates' element={<OepUpdates />} />
                      <Route
                        path='/marketing/content-library'
                        element={<ContentLibrary />}
                      />
                      {showLeadPartnersTab && (
                        <Route
                          path='/marketing/leads'
                          element={<MarketingLeadPartners />}
                        />
                      )}
                    </>
                  )}
                  {company === 'sunshine' && (
                    <>
                      <Route
                        path='/reconciliation-forms'
                        element={<ReconciliationForms />}
                      />
                      {showCallReviews && (
                        <Route path='/call-reviews' element={<CallReviews />} />
                      )}
                    </>
                  )}
                </>
              )}
            </Route>
            {company === 'sunshine' && (
              <Route path='/privacy' element={<PrivacyPolicy />} />
            )}
            {/* outside PrivateRoute/PrivateRouteAuth: the view guards itself */}
            <Route
              path='/force-change-password'
              element={<ForceChangePassword />}
            />
            <Route path='*' element={<DefaultRedirect user={user} />} />
          </Routes>
        </Router>
      )}
    </>
  );
}

export default RoutesComponent;
